import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { FaInfoCircle, FaUserShield, FaClock, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';
import './Cases.css';

const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
const STATUS_LABEL = {
  open: 'Open',
  in_progress: 'In progress',
  pending_review: 'Pending review',
  closed: 'Closed',
  reopened: 'Reopened'
};
const OUTCOMES = [
  { v: 'confirmed_risk',          label: 'Confirmed risk' },
  { v: 'false_positive',          label: 'False positive' },
  { v: 'no_further_action',       label: 'No further action' },
  { v: 'escalated_to_regulator',  label: 'Escalated to regulator' }
];

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const customerName = (c) =>
  c.customer_name_individual || c.customer_name_business || `Customer #${c.customer_id}`;

const isOverdue = (c) =>
  c.due_at && new Date(c.due_at) < new Date() && !['closed','reopened'].includes(c.status);

function ManageCases() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const canRead   = hasPermission(user, 'cases.read');
  const canUpdate = hasPermission(user, 'cases.update');
  const canAssign = hasPermission(user, 'cases.assign');
  const canClose  = hasPermission(user, 'cases.close');
  const canReopen = hasPermission(user, 'cases.reopen');
  const canCreateStr = hasPermission(user, 'str_reports.create');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Filters work on the loaded list straight away (same as the KYC page)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignFilter, setAssignFilter] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null);    // { title, fields, onConfirm }
  const [actionVals, setActionVals] = useState({});
  const [actionBusy, setActionBusy] = useState(false);
  // User typeahead state — populated when an action modal contains a 'user-typeahead' field.
  const [userOptions, setUserOptions] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userListLoading, setUserListLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/cases/stats'); setStats(r.data); }
    catch (e) { console.error(e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/cases?limit=500');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, []);

  const isMine = (c) => !!user && c.assigned_to === user.id;
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((c) =>
      (statusFilter === 'all' || c.status === statusFilter) &&
      (priorityFilter === 'all' || c.priority === priorityFilter) &&
      (assignFilter === 'all' ||
        (assignFilter === 'me' && isMine(c)) ||
        (assignFilter === 'unassigned' && !c.assigned_to)) &&
      (dueFilter === 'all' || (dueFilter === 'overdue' ? isOverdue(c) : !isOverdue(c))) &&
      (!q || [c.case_number, c.title, customerName(c), c.assigned_to_email]
        .some((v) => String(v || '').toLowerCase().includes(q)))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchTerm, statusFilter, priorityFilter, assignFilter, dueFilter, user]);
  const countBy = (fn) => rows.filter(fn).length;

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchList();
  }, [canRead, fetchStats, fetchList]);

  // Deep-link support: /cases?id=42 opens that case directly (used by the
  // "Promote to case" button in ManageCustomers).
  const routeLocation = useLocation();
  useEffect(() => {
    if (!canRead) return;
    const qs = new URLSearchParams(routeLocation.search);
    const idParam = qs.get('id');
    if (idParam && /^\d+$/.test(idParam)) openDetail(Number(idParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, routeLocation.search]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await axios.get(`/cases/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(e.message || 'Failed to load case');
      setSelectedId(null);
    }
  };

  const openModal = (cfg) => {
    setActionVals(cfg.initial || {});
    setUserSearch('');
    setActionModal(cfg);
    // If the modal contains a user-typeahead field, lazy-load the user list.
    if ((cfg.fields || []).some(f => f.type === 'user-typeahead') && userOptions.length === 0) {
      setUserListLoading(true);
      axios.get('/users?limit=200')
        .then(r => setUserOptions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setUserOptions([]))
        .finally(() => setUserListLoading(false));
    }
  };

  const runAction = async () => {
    if (!actionModal?.onConfirm) return;
    try {
      setActionBusy(true);
      await actionModal.onConfirm(actionVals);
      if (detail) {
        const r = await axios.get(`/cases/${detail.id}`);
        setDetail(r.data);
      }
      fetchList(); fetchStats();
      setActionModal(null);
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to access Case Management.
            Ask your administrator for the <code>cases.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stat cards */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--teal">
              <div className="stat-label">Total cases</div>
              <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
            </div>
            <div className="stat-card stat-card--blue">
              <div className="stat-label">My open</div>
              <div className="stat-value stat-value--blue">{stats ? stats.my_open : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Overdue</div>
              <div className="stat-value stat-value--orange">{stats ? stats.overdue : '—'}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Critical</div>
              <div className="stat-value stat-value--purple">{stats?.by_priority?.critical || 0}</div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Search bar — same as the KYC page */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="table-controls">
          <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-filter"
              style={{ width: '100%', maxWidth: 'none', paddingRight: '2.25rem', boxSizing: 'border-box' }}
            />
            {searchTerm && (
              <span
                role="button"
                tabIndex={0}
                title="Clear search"
                aria-label="Clear search"
                onClick={() => setSearchTerm('')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSearchTerm(''); }}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-secondary, #607d8b)', display: 'flex' }}
              >
                <FaTimes />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters — instant, with counts (same as the KYC page) */}
      {rows.length > 0 && (
        <div className="search-results-info" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
            Showing {filtered.length} of {rows.length} case{rows.length === 1 ? '' : 's'}
          </div>
          <select
            className="table-search-input"
            style={{ width: 'auto' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            title="Filter by status"
          >
            <option value="all">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l} ({countBy((c) => c.status === v)})</option>
            ))}
          </select>
          <select
            className="table-search-input"
            style={{ width: 'auto' }}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            title="Filter by priority"
          >
            <option value="all">All priorities</option>
            {Object.entries(PRIORITY_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l} ({countBy((c) => c.priority === v)})</option>
            ))}
          </select>
          <select
            className="table-search-input"
            style={{ width: 'auto' }}
            value={assignFilter}
            onChange={(e) => setAssignFilter(e.target.value)}
            title="Filter by assignment"
          >
            <option value="all">Assigned: anyone</option>
            <option value="me">Assigned to me ({countBy(isMine)})</option>
            <option value="unassigned">Unassigned ({countBy((c) => !c.assigned_to)})</option>
          </select>
          <select
            className="table-search-input"
            style={{ width: 'auto' }}
            value={dueFilter}
            onChange={(e) => setDueFilter(e.target.value)}
            title="Filter by due date"
          >
            <option value="all">Due: all</option>
            <option value="overdue">Overdue ({countBy(isOverdue)})</option>
            <option value="on_time">Not overdue ({countBy((c) => !isOverdue(c))})</option>
          </select>
        </div>
      )}

      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* Case list */}
      <div className="case-list">
        {loading && rows.length === 0 && <p>Loading cases…</p>}
        {!loading && rows.length === 0 && (
          <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No cases yet.</p>
        )}
        {rows.length > 0 && filtered.length === 0 && (
          <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No cases match the current filters.</p>
        )}
        {filtered.map(c => (
          <div key={c.id} className={`case-card priority-${c.priority}${isOverdue(c) ? ' is-overdue' : ''}`} onClick={() => openDetail(c.id)}>
            <div className="case-card__top">
              <div>
                <div className="case-card__number">{c.case_number}</div>
                <h4 className="case-card__title">{c.title}</h4>
                <div className="case-card__customer">
                  <FaUserShield /> {customerName(c)}
                </div>
              </div>
              <div className="case-card__pills">
                <span className={`case-pill case-pill--prio-${c.priority}`}>{PRIORITY_LABEL[c.priority]}</span>
                <span className={`case-pill case-pill--status-${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span>
                {isOverdue(c) && <span className="case-pill case-pill--overdue"><FaExclamationTriangle /> Overdue</span>}
              </div>
            </div>
            <div className="case-card__meta">
              <span><FaClock /> Opened {fmt(c.opened_at)}</span>
              <span>Due {fmt(c.due_at)}</span>
              <span>Assigned: {c.assigned_to_email || 'Unassigned'}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Case detail modal */}
      {selectedId && (
        <div className="modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedId(null)}>×</button>
            <div className="modal-body" style={{ paddingTop: '1.5rem' }}>
              {!detail ? <p>Loading…</p> : (
                <>
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">{detail.case_number} — {detail.title}</h4>
                      <p className="form-section__subtitle">
                        Customer: <strong>{customerName(detail)}</strong>
                        {detail.customer_type && ` (${detail.customer_type})`}
                      </p>
                    </header>
                    <div className="cust-detail-grid">
                      <div className="cust-detail-field"><span className="cust-detail-label">Status</span><span className="cust-detail-value">{STATUS_LABEL[detail.status] || detail.status}</span></div>
                      <div className="cust-detail-field"><span className="cust-detail-label">Priority</span><span className="cust-detail-value">{PRIORITY_LABEL[detail.priority]}</span></div>
                      <div className="cust-detail-field"><span className="cust-detail-label">Assigned to</span><span className="cust-detail-value">{detail.assigned_to_email || 'Unassigned'}</span></div>
                      <div className="cust-detail-field"><span className="cust-detail-label">Opened</span><span className="cust-detail-value">{fmt(detail.opened_at)} {detail.opened_by_email ? `by ${detail.opened_by_email}` : ''}</span></div>
                      <div className="cust-detail-field"><span className="cust-detail-label">Due</span><span className="cust-detail-value">{fmt(detail.due_at)}</span></div>
                      <div className="cust-detail-field"><span className="cust-detail-label">Source</span><span className="cust-detail-value">{detail.source_type}{detail.source_id ? ` #${detail.source_id}` : ''}</span></div>
                      {detail.closed_at && (
                        <>
                          <div className="cust-detail-field"><span className="cust-detail-label">Closed</span><span className="cust-detail-value">{fmt(detail.closed_at)} {detail.closed_by_email ? `by ${detail.closed_by_email}` : ''}</span></div>
                          <div className="cust-detail-field"><span className="cust-detail-label">Outcome</span><span className="cust-detail-value">{detail.closure_outcome}</span></div>
                        </>
                      )}
                    </div>
                    {detail.description && (
                      <p style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>{detail.description}</p>
                    )}
                  </section>

                  {/* Actions */}
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Actions</h4>
                    </header>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {canAssign && (
                        <button className="export-button" onClick={() => openModal({
                          title: 'Assign case',
                          fields: [{ key: 'assigned_to', label: 'Search by name or email (clear to unassign)', type: 'user-typeahead' }],
                          initial: { assigned_to: detail.assigned_to || null },
                          onConfirm: async (v) => {
                            const id = v.assigned_to ? Number(v.assigned_to) : null;
                            await axios.post(`/cases/${detail.id}/assign`, { assigned_to: id });
                          }
                        })}>Assign</button>
                      )}
                      {canUpdate && detail.status !== 'closed' && (
                        <>
                          <button className="export-button" onClick={() => openModal({
                            title: 'Add note',
                            fields: [{ key: 'description', label: 'Note', type: 'textarea' }],
                            onConfirm: async (v) => {
                              if (!v.description) return;
                              await axios.post(`/cases/${detail.id}/events`, { description: v.description });
                            }
                          })}>Add note</button>
                          <button className="export-button" onClick={() => openModal({
                            title: 'Change status',
                            fields: [{ key: 'status', label: 'New status', type: 'select', options: Object.entries(STATUS_LABEL).filter(([v]) => v !== 'reopened').map(([v, l]) => ({ v, label: l })) }],
                            initial: { status: detail.status },
                            onConfirm: async (v) => {
                              await axios.patch(`/cases/${detail.id}`, { status: v.status });
                            }
                          })}>Change status</button>
                          <button className="export-button" onClick={() => openModal({
                            title: 'Change priority',
                            fields: [{ key: 'priority', label: 'New priority', type: 'select', options: Object.entries(PRIORITY_LABEL).map(([v, l]) => ({ v, label: l })) }],
                            initial: { priority: detail.priority },
                            onConfirm: async (v) => {
                              await axios.patch(`/cases/${detail.id}`, { priority: v.priority });
                            }
                          })}>Change priority</button>
                        </>
                      )}
                      {canClose && detail.status !== 'closed' && (
                        <button className="btn-cancel" onClick={() => openModal({
                          title: 'Close case',
                          fields: [
                            { key: 'outcome', label: 'Outcome', type: 'select', options: OUTCOMES.map(o => ({ v: o.v, label: o.label })) },
                            { key: 'notes', label: 'Closure notes (optional)', type: 'textarea' }
                          ],
                          initial: { outcome: 'no_further_action' },
                          onConfirm: async (v) => {
                            await axios.post(`/cases/${detail.id}/close`, { outcome: v.outcome, notes: v.notes || null });
                          }
                        })}>Close</button>
                      )}
                      {canReopen && detail.status === 'closed' && (
                        <button className="export-button" onClick={() => openModal({
                          title: 'Reopen case',
                          fields: [{ key: 'reason', label: 'Reason (optional)', type: 'textarea' }],
                          onConfirm: async (v) => {
                            await axios.post(`/cases/${detail.id}/reopen`, { reason: v.reason || null });
                          }
                        })}>Reopen</button>
                      )}
                      {/* STR is only available once a case is closed with
                          outcome 'escalated_to_regulator'. The generate call
                          is idempotent — re-clicking opens the existing draft. */}
                      {canCreateStr && detail.status === 'closed'
                        && detail.closure_outcome === 'escalated_to_regulator' && (
                        <button className="export-button" onClick={async () => {
                          try {
                            const r = await axios.post('/str-reports/generate', { case_id: detail.id });
                            const id = r?.data?.report?.id;
                            if (id) navigate(`/str-reports?id=${id}`);
                          } catch (e) {
                            setError(e.response?.data?.error?.message || e.message || 'Failed to generate STR');
                          }
                        }}>Generate STR</button>
                      )}
                    </div>
                  </section>

                  {/* Timeline */}
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Timeline</h4>
                      <p className="form-section__subtitle">{detail.events?.length || 0} event{detail.events?.length === 1 ? '' : 's'}</p>
                    </header>
                    <ul className="case-timeline">
                      {detail.events?.map(ev => (
                        <li key={ev.id}>
                          <span className="case-timeline__when">{fmt(ev.created_at)}</span>
                          <span className="case-timeline__type">{ev.event_type.replace(/_/g, ' ')}</span>
                          <span className="case-timeline__actor">{ev.actor_email || 'system'}</span>
                          <div className="case-timeline__desc">{ev.description || '—'}</div>
                        </li>
                      ))}
                    </ul>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div className="modal-overlay" onClick={() => !actionBusy && setActionModal(null)}>
          <div className="modal-content" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => !actionBusy && setActionModal(null)}>×</button>
            <div className="modal-body" style={{ paddingTop: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700 }}>{actionModal.title}</h3>
              {actionModal.fields.map(f => {
                if (f.type === 'user-typeahead') {
                  const selectedId = actionVals[f.key];
                  const selectedUser = selectedId
                    ? userOptions.find(u => Number(u.id) === Number(selectedId))
                    : null;
                  const term = userSearch.trim().toLowerCase();
                  const matches = term
                    ? userOptions
                        .filter(u => {
                          const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                          return name.includes(term) || (u.email || '').toLowerCase().includes(term);
                        })
                        .slice(0, 8)
                    : [];
                  return (
                    <div key={f.key} className="form-group">
                      <label>{f.label}</label>
                      {selectedUser ? (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.4rem 0.6rem', border: '1px solid var(--accent,#00796b)',
                          background: 'var(--accent-muted,rgba(0,121,107,0.08))', fontSize: '0.875rem'
                        }}>
                          <span>
                            <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>
                            <span style={{ color: 'var(--text-secondary,#607d8b)', marginLeft: '0.4rem' }}>
                              {selectedUser.email}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => { setActionVals({ ...actionVals, [f.key]: null }); setUserSearch(''); }}
                            disabled={actionBusy}
                            style={{
                              background: 'transparent', border: 'none', cursor: 'pointer',
                              fontSize: '1rem', lineHeight: 1, padding: '0 0.2rem',
                              color: 'var(--text-secondary,#607d8b)'
                            }}
                            title="Unassign"
                          >×</button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            placeholder={userListLoading ? 'Loading users…' : 'Start typing a name or email'}
                            disabled={actionBusy || userListLoading}
                            autoFocus
                          />
                          {term && (
                            <div style={{
                              border: '1px solid var(--input-border,#cfd6db)',
                              borderTop: 'none',
                              maxHeight: '220px', overflow: 'auto',
                              background: 'var(--card-bg,#fff)'
                            }}>
                              {matches.length === 0 && (
                                <div style={{
                                  padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                                  color: 'var(--text-secondary,#607d8b)'
                                }}>
                                  No users match "{userSearch}".
                                </div>
                              )}
                              {matches.map(u => (
                                <div
                                  key={u.id}
                                  onClick={() => {
                                    setActionVals({ ...actionVals, [f.key]: u.id });
                                    setUserSearch('');
                                  }}
                                  style={{
                                    padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem',
                                    borderBottom: '1px solid var(--border-color,#eef0f3)'
                                  }}
                                  onMouseDown={e => e.preventDefault()}
                                >
                                  <strong>{u.first_name} {u.last_name}</strong>
                                  <span style={{ color: 'var(--text-secondary,#607d8b)', marginLeft: '0.5rem' }}>
                                    {u.email}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' }}>
                            Leave empty to unassign the case.
                          </p>
                        </>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={f.key} className="form-group">
                    <label>{f.label}</label>
                    {f.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        value={actionVals[f.key] || ''}
                        onChange={e => setActionVals({ ...actionVals, [f.key]: e.target.value })}
                        disabled={actionBusy}
                      />
                    ) : f.type === 'select' ? (
                      <select
                        value={actionVals[f.key] || ''}
                        onChange={e => setActionVals({ ...actionVals, [f.key]: e.target.value })}
                        disabled={actionBusy}
                      >
                        {f.options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input
                        type={f.type || 'text'}
                        value={actionVals[f.key] || ''}
                        onChange={e => setActionVals({ ...actionVals, [f.key]: e.target.value })}
                        disabled={actionBusy}
                      />
                    )}
                  </div>
                );
              })}
              <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn-cancel" onClick={() => setActionModal(null)} disabled={actionBusy}>Cancel</button>
                <button type="button" className={`export-button${actionBusy ? ' is-loading' : ''}`} onClick={runAction} disabled={actionBusy}>
                  {actionBusy ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageCases;
